import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  LightMode,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import SearchInfiniteProvider from "@raiden/library/contexts/SearchInfinite/SearchInfiniteProvider";
import { useSearchInfinite } from "@raiden/library/contexts/SearchInfinite/useSearchInfinite";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DataHandlerInfinite } from "../../../../../../components/DataHandlerInfinite";
import {
  mergeProps,
  PopoverInput,
} from "../../../../../../components/Form/PopoverInput";
import { Icon } from "../../../../../../components/Icon";
import { SearchResultItem } from "./Item";

/**
 * @typedef {object} SearchResultsSearchFormValues
 * @property {string} term
 */
/** @type {SearchResultsSearchFormValues} */
export const SEARCH_RESULT_SEARCH_FORM_DEFAULT_VALUES = {
  term: "",
};

export function SearchResult() {
  const intl = useIntl();

  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [inputWidth, setInputWidth] = useState(0);

  const {
    isOpen: isOpenPopoverInput,
    onClose: onClosePopoverInput,
    onOpen,
  } = useDisclosure();

  /** @type {import("@raiden/library/contexts/SearchInfinite/SearchInfiniteProvider").SearchInfiniteContextValue<SearchResultsSearchFormValues, import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/search/Result").SearchResult[]>>} */
  const searchInfiniteContext = useSearchInfinite({
    defaultValues: SEARCH_RESULT_SEARCH_FORM_DEFAULT_VALUES,
    initialFields: useMemo(
      () => ({
        term: "",
      }),
      [],
    ),
    generateUrl: useCallback((fields, key) => {
      if (fields.term.length < 2) {
        return null;
      }
      return generateApiUrl({
        id: "@api.search",
        query: {
          ...fields,
          page: key + 1,
          per_page: 10,
          fields: ["search.result.categories", "search.result.translations"],
        },
      });
    }, []),
    useRouterUrl: false,
    autoSubmit: true,
  });

  const onOpenPopoverInput = useCallback(() => {
    if (searchInfiniteContext.form.getValues("term").length >= 1) {
      onOpen();
    }
  }, [onOpen, searchInfiniteContext.form]);

  function handleOnClose() {
    searchInfiniteContext.form.reset({
      term: "",
    });
    onClosePopoverInput();
  }

  // Retrieve the width of the input element when the popover is opened
  useEffect(() => {
    if (inputRef.current) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [isOpenPopoverInput]);

  return (
    <LightMode>
      <SearchInfiniteProvider {...searchInfiniteContext}>
        <PopoverInput
          isOpen={isOpenPopoverInput}
          onOpen={onOpenPopoverInput}
          onClose={onClosePopoverInput}
          popoverContentProps={{
            mt: "-5px",
            borderRadius: "12px",
            maxWidth: `${inputWidth}px`,
          }}
          popoverBody={
            <DataHandlerInfinite
              swrInfiniteResponse={searchInfiniteContext.swrInfiniteResponse}
              noResult={
                <Text
                  py=".5rem"
                  color="brandPrimary.700"
                  textAlign="center"
                  fontWeight={500}>
                  <FormattedMessage defaultMessage="Aucun résultat trouvé." />
                </Text>
              }>
              {(pages) => (
                <SearchResultItem
                  pages={pages}
                  error={searchInfiniteContext.swrInfiniteResponse.error}
                  isValidating={
                    searchInfiniteContext.swrInfiniteResponse.isValidating
                  }
                  setSize={searchInfiniteContext.swrInfiniteResponse.setSize}
                  onClose={onClosePopoverInput}
                />
              )}
            </DataHandlerInfinite>
          }
          renderInput={({ inputProps }) => (
            <FormController
              ref={inputRef}
              control={searchInfiniteContext.form.control}
              name="term"
              rules={{
                onChange: (event) => {
                  if (event.target["value"].length < 2) {
                    onClosePopoverInput();
                  }
                },
              }}
              render={(field) => (
                <InputGroup>
                  <Input
                    {...field}
                    type="search"
                    {...mergeProps(field, inputProps)}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Que recherchez-vous ?",
                    })}
                    borderRadius="16px"
                    h="2.5rem"
                    fontSize="16px"
                    fontWeight="400"
                    backgroundColor="#fff"
                    autoComplete="off"
                    _focus={{
                      zIndex: "initial",
                    }}
                    css={{
                      "&::-webkit-search-cancel-button": {
                        display: "none",
                      },
                    }}
                  />

                  <InputLeftElement zIndex="initial">
                    <Icon
                      icon="ms_search"
                      size="24px"
                      color="brandPrimary.700"
                    />
                  </InputLeftElement>

                  {searchInfiniteContext.form.getValues("term").length > 0 && (
                    <InputRightElement zIndex="initial">
                      <IconButton
                        mr=".5rem"
                        icon={
                          <Icon
                            icon="ms_close"
                            size="20px"
                            color="brandPrimary.700"
                          />
                        }
                        aria-label={intl.formatMessage({
                          defaultMessage: "Réinitialiser la recherche",
                        })}
                        size="sm"
                        bg="inherit"
                        onClick={handleOnClose}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              )}
            />
          )}
        />
      </SearchInfiniteProvider>
    </LightMode>
  );
}
